import {Component, Directive, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {Principal} from './core/auth/principal.service';
import {LoginService} from './core/login/login.service';
import {PublisherListFilter, PublishersService} from './shared/services/publishers.service';
import {AuthService} from './core/auth/auth.service';
import {Publisher} from './shared/domain/publisher.model';
import {SnackBarService} from './core/services/snackbar.service';
import {User} from './core/auth/user.model';
import {MatDialog} from '@angular/material/dialog';
import {PlaylistIngestFeedDialogComponent} from './modules/playlists/playlist-ingest-feed-dialog/playlist-ingest-feed-dialog.component';
import {PlaylistEditorDialogComponent} from './modules/playlists/playlist-editor-dialog.component';
import {ChannelEditorDialogComponent} from './modules/channels/channel-editor-dialog.component';
import {NavigationEnd, Router} from '@angular/router';
import {UserEditorDialogComponent} from './modules/users/user-editor/user-editor-dialog.component';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { BreadcrumbItem } from './shared/components/breadcrumbs/breadcrumbs.component';
import { PageBreadcrumbService } from './shared/components/breadcrumbs/page-breadcrumb.service';
import {ContentOwnerService} from './shared/services/content-owner.service';
import {ContentOwner} from './shared/domain/content-owner.model';
import {AppRoutePath} from './app-route-path.enum';
import { UserRole } from './core/auth/user-role.model';
import { RolePermission } from './core/auth/rbac/role-permission.service';
import { MatAccordion, MatExpansionPanel } from '@angular/material/expansion';
import { GlobalSidenavService } from './core/services/global-sidenav.service';
import { ChangePasswordDialogComponent } from './shared/components/change-password-dialog/change-password-dialog.component';
import { ConsentService } from './core/consent/consent.service';
import {MatDialogConfig} from '@angular/material/dialog';
import { MatSidenav } from '@angular/material/sidenav';

@Component({
    selector: 'ctv-main',
    templateUrl: './main.component.html',
    styleUrls: ['./main.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MainComponent implements OnInit, OnDestroy {
  @ViewChild('sidenav') sidenavRef: MatSidenav;
  @ViewChild('accordion') accordion: MatAccordion;
  @ViewChild('accordionNetwork') accordionNetwork: MatAccordion;

  public readonly AppRoutePath = AppRoutePath;
  public readonly RolePermission = RolePermission;
  isSideNavOpened$: Observable<boolean>;
  renderNav$: Observable<boolean>;
  breadcrumbItems$: Observable<BreadcrumbItem[]>;

  private static readonly SECTION_HOME_LINKS: Record<string, string> = {
    Inventory: '/placements',
    Partners: '/publishers',
    Demand: '/demand/list',
    Campaigns: '/advertiser',
    Analytics: '/reports',
    A2V: '/assets',
    Content: '/owned-operated-apps',
    Moments: '/sources',
  };

  private static readonly PAGE_BY_SEGMENT: Record<string, { section: string; page: string; pageLink: string }> = {
    placements: { section: 'Inventory', page: 'Placements', pageLink: '/placements' },
    'audio-supply': { section: 'Inventory', page: 'Audio Supply', pageLink: '/audio-supply' },
    'display-supply': { section: 'Inventory', page: 'Display Supply', pageLink: '/display-supply' },
    'supply-search': { section: 'Inventory', page: 'Supply Search', pageLink: '/supply-search' },
    apps: { section: 'Inventory', page: 'Apps', pageLink: '/apps' },
    'supply-marketplace': { section: 'Inventory', page: 'Supply Marketplace', pageLink: '/supply-marketplace' },
    forecast: { section: 'Inventory', page: 'Forecast', pageLink: '/forecast' },
    targeting: { section: 'Inventory', page: 'Targeting', pageLink: '/targeting' },
    'block-list': { section: 'Inventory', page: 'Block List', pageLink: '/block-list' },
    'ads-txt': { section: 'Inventory', page: 'Ads.txt', pageLink: '/ads-txt' },
    marketplace: { section: 'Inventory', page: 'Marketplace', pageLink: '/marketplace' },
    taxonomy: { section: 'Inventory', page: 'Taxonomy', pageLink: '/taxonomy' },
    publishers: { section: 'Partners', page: 'Publishers', pageLink: '/publishers' },
    networks: { section: 'Partners', page: 'Network', pageLink: '/networks' },
    connections: { section: 'Demand', page: 'Connections', pageLink: '/connections' },
    deals: { section: 'Demand', page: 'Deals', pageLink: '/deals' },
    advertiser: { section: 'Campaigns', page: 'Advertisers', pageLink: '/advertiser' },
    orders: { section: 'Campaigns', page: 'Campaigns', pageLink: '/orders' },
    'line-items': { section: 'Campaigns', page: 'Line Items', pageLink: '/line-items' },
    campaigns: { section: 'Campaigns', page: 'Creatives', pageLink: '/campaigns/creative-library' },
    reports: { section: 'Analytics', page: 'Report Builder', pageLink: '/reports' },
    analytics: { section: 'Analytics', page: 'Analytics', pageLink: '/analytics' },
    performance: { section: 'Analytics', page: 'Performance Analyzer', pageLink: '/performance' },
    'scheduled-reports': { section: 'Analytics', page: 'Scheduled Reports', pageLink: '/scheduled-reports' },
    assets: { section: 'A2V', page: 'Assets', pageLink: '/assets' },
    brands: { section: 'A2V', page: 'Brands', pageLink: '/brands' },
    'network-template': { section: 'A2V', page: 'Network Template', pageLink: '/network-template' },
    'owned-operated-apps': { section: 'Content', page: 'O&O Apps', pageLink: '/owned-operated-apps' },
    collections: { section: 'Content', page: 'Collections', pageLink: '/collections' },
    playlists: { section: 'Content', page: 'Collections', pageLink: '/playlists' },
    videos: { section: 'Content', page: 'Videos', pageLink: '/videos' },
    'content-owner': { section: 'Content', page: 'Content Owners', pageLink: '/content-owner' },
    sources: { section: 'Moments', page: 'Sources', pageLink: '/sources' },
    users: { section: '', page: 'Users', pageLink: '/users' },
    changelog: { section: '', page: 'Change Logs', pageLink: '/changelog' },
    logs: { section: '', page: 'System Logs', pageLink: '/logs' },
    'debug-capture': { section: '', page: 'Debug Capture', pageLink: '/debug-capture' },
    'supply-upload': { section: 'Inventory', page: 'Bulk Upload', pageLink: '/supply-upload' },
  };

  private static readonly DEMAND_PAGE_BY_SEGMENT: Record<string, { page: string; pageLink: string }> = {
    list: { page: 'Partners', pageLink: '/demand/list' },
    'import-status': { page: 'Partners', pageLink: '/demand/import-status' },
    compliance: { page: 'Compliance', pageLink: '/demand/compliance' },
    discrepancies: { page: 'Discrepancies', pageLink: '/demand/discrepancies' },
    'health-settings': { page: 'Health Settings', pageLink: '/demand/health-settings' },
  };

  private static readonly CREATIVE_TAB_BY_SEGMENT: Record<string, string> = {
    'creatives-table': 'VAST & Standard',
    'interactive-table': 'Interactive',
    'dynamic-table': 'Dynamic',
    'moments-table': 'Moments',
    'template-table': 'Templates',
  };

  private static readonly TAXONOMY_TAB_BY_SEGMENT: Record<string, string> = {
    table: 'Genres',
    'unmapped-table': 'Requests',
  };

  private static demandTabLabel(pageSegment: string, tabSegment?: string): string | undefined {
    if (pageSegment === 'list') {
      return 'List';
    }
    if (pageSegment === 'import-status') {
      return 'Import Status';
    }
    if (pageSegment === 'compliance') {
      return tabSegment === 'findings' ? 'Findings' : 'Domain Compliance';
    }
    if (pageSegment === 'discrepancies') {
      return 'DP Issues';
    }
    return undefined;
  }

  private static inventoryTabLabel(page: string, tabSegment?: string): string | undefined {
    if (page === 'ads-txt') {
      if (tabSegment === 'records') {
        return 'Records';
      }
      if (tabSegment === 'domains') {
        return 'Domains';
      }
      if (!tabSegment || tabSegment === 'apps') {
        return 'Apps';
      }
      return undefined;
    }
    if (page === 'apps') {
      if (tabSegment === 'requests') {
        return 'App Requests';
      }
      if (tabSegment === 'compliance') {
        return 'Compliance';
      }
      if (!tabSegment || tabSegment === 'table') {
        return 'Apps';
      }
    }
    return undefined;
  }

  private static readonly CAMPAIGNS_PAGE_BY_SEGMENT: Record<string, { page: string; pageLink: string }> = {
    'creative-library': { page: 'Creatives', pageLink: '/campaigns/creative-library' },
    'new-creative': { page: 'Creatives', pageLink: '/campaigns/creative-library' },
    'moderation-queue': { page: 'Review Queue', pageLink: '/campaigns/moderation-queue' },
    'creative-designer': { page: 'Creatives', pageLink: '/campaigns/creative-library' },
  };
  screenWidth: number;
  mobileTopBar = document.getElementsByClassName('mobile-top-bar');
  scrollOffset: number;
  publishers: Publisher[];
  contentOwners: ContentOwner[];
  user: User;
  subscription = new Subscription();

  UI: {
    isNetworkUser?: boolean;
    isReadonlyAdmin?: boolean;
    isSalesRep?: boolean;
    isCampaignManager?: boolean;
    isRevOps?: boolean;
    isAdmin?: boolean;
    isDesigner?: boolean;
    isAdvertiser?: boolean;
    isFinance?: boolean;
  } = {};

  constructor(public principal: Principal,
              private httpClient: HttpClient,
              private loginService: LoginService,
              private authService: AuthService,
              private publisherService: PublishersService,
              private contentOwnerService: ContentOwnerService,
              private sbs: SnackBarService,
              private matDialog: MatDialog,
              private router: Router,
              private pageBreadcrumb: PageBreadcrumbService,
              public sideNavService: GlobalSidenavService,
              private consentService: ConsentService) {
    this.screenWidth = window.innerWidth;
    this.sideNavService.setSideNavState(!this.screenWidth || this.screenWidth > 992);
    this.isSideNavOpened$ = this.sideNavService.isSideNavOpen$;
    this.renderNav$ = this.sideNavService.renderNav$;
    this.breadcrumbItems$ = combineLatest([
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => this.router.url),
        startWith(this.router.url)
      ),
      this.pageBreadcrumb.items$
    ]).pipe(
      map(([url, extra]) => this.buildBreadcrumbItems(url, extra))
    );

    this.authService.updateUserHeaderData.subscribe((updateUserHeaderData: boolean) => {
      this.publishers = [];
      this.contentOwners = [];
    });
  }


  ngOnInit(): void {
    this.user = this.principal.user;
    this.UI.isNetworkUser = !!this.user.network;
    // SALES_REPRESENTATIVE/DESIGNER/REV_OPS/CAMPAIGN_MANAGER no longer exist as backend roles -
    // folded into READONLY_USER (former sales rep/rev ops) and USER (former designer/campaign
    // manager) by the 2026-08-02 RBAC reconciliation (see user-role.model.ts). Real accounts will
    // never match the legacy checks below, so both former personas in each pair now show the
    // same (unioned) nav - flagged for product/QA sign-off, see role-permission.service.ts.
    this.UI.isSalesRep = !!this.user.roles.includes(UserRole.READONLY_USER);
    this.UI.isDesigner = !!this.user.roles.includes(UserRole.USER);
    this.UI.isRevOps = !!this.user.roles.includes(UserRole.READONLY_USER);
    this.UI.isCampaignManager = !!this.user.roles.includes(UserRole.USER);
    this.UI.isAdmin = !!this.user.roles.includes(UserRole.ADMIN);
    this.UI.isReadonlyAdmin = !!this.user.roles.includes(UserRole.READ_ONLY_ADMIN);
    // SRS-47: advertiser nav includes creatives; finance-only nav omits creative management.
    this.UI.isAdvertiser = !!this.user.roles.includes(UserRole.ADVERTISER);
    this.UI.isFinance = !!this.user.roles.includes(UserRole.FINANCE) && !this.UI.isAdvertiser;
    // Defer shell dropdown lists so the active route's data request is not competing for bandwidth.
    setTimeout(() => {
      this.getUserPublishers();
      this.getUserCo();
    });
  }

  getUserPublishers() {
    if (this.principal.user.publisherIds && this.principal.user.publisherIds.length) {
      this.subscription.add(
        this.publisherService.listPublishers({ids: this.principal.user.publisherIds} as PublisherListFilter).subscribe(publishers => {
          this.publishers = publishers.content;
        }, () => {
          this.sbs.error('Error during while fetching publishers');
        })
      );
    }
  }

  getUserCo() {
    if (this.principal.user.contentOwnerIds && this.principal.user.contentOwnerIds.length) {
      this.subscription.add(
        this.contentOwnerService.list({coIds: this.principal.user.contentOwnerIds}).subscribe(response => {
          this.contentOwners = response.filter(co => this.principal.user.contentOwnerIds.includes(co.id));
        }, () => {
          this.sbs.error('Error during while fetching content owners');
        })
      );
    }
  }

  public openChangePasswordDialog() {
    this.matDialog.open(ChangePasswordDialogComponent, ({
      height: 'auto'
    } as MatDialogConfig));
  }

  public openPrivacyChoices(): void {
    const snapshot = this.consentService.snapshot$.value;
    if (!snapshot.regionResolved) {
      this.sbs.info('Checking whether privacy choices apply in your region.');
      return;
    }
    if (!snapshot.inScope) {
      this.sbs.info('Privacy choices are only required in certain regions.');
      return;
    }
    this.consentService.reopen();
  }

  public logout() {
    this.loginService.logout();
  }

  @HostListener('window:resize')
  resizeScreen() {
    this.screenWidth = window.innerWidth;
    this.sideNavService.setSideNavState(!this.screenWidth || this.screenWidth > 992);
  }

  @HostListener('window:scroll', ['$event']) onScrollEvent($event) {
    if ($event.path && $event.path.length) {
      this.scrollOffset = $event.path[1].scrollY;
    } else {
      this.scrollOffset = 0;
    }
  }

  openDashboard(type: 'co' | 'publisher', id?: string) {
    if (type === 'publisher') {
      window.open(`/#/p/${id}`, '_blank').focus();
    } else {
      window.open(`/#/co/${id}`, '_blank').focus();
    }
  }

  openDialog(type: string) {
    let modalComponent;
    switch (type) {
      case 'feed':
        modalComponent = PlaylistIngestFeedDialogComponent;
        break;
      case 'playlist':
        modalComponent = PlaylistEditorDialogComponent;
        break;
      case 'channel':
        modalComponent = ChannelEditorDialogComponent;
        break;
      case 'user':
        modalComponent = UserEditorDialogComponent;
        break;
      default:
        break;
    }
    // todo add publisher created

    if (modalComponent) {
      this.matDialog.open(modalComponent);
    }

    if (type === 'content-owner') {
      this.router.navigate(['content-owner/create']);
    }
  }

  public isCampaignsNavActive(): boolean {
    const path = (this.router.url || '').split(/[?#]/)[0];
    return /^\/(advertiser|orders|line-items|campaigns)(\/|$)/.test(path);
  }

  closeSidenavMobile(sidenav: any) {
    if (this.screenWidth < 992) {
      this.sideNavService.close();
    }
  }

  closeAllExpansionsPanels(){
    this.accordion.multi = true;
    this.accordion.closeAll();
    this.accordion.multi = false;
    this.clearNavGradientOwners();
  }

  closeAllNetworkExpansionsPanels(){
    this.accordionNetwork.multi = true;
    this.accordionNetwork.closeAll();
    this.accordionNetwork.multi = false;
    this.clearNavGradientOwners();
  }

  private clearNavGradientOwners() {
    document.querySelectorAll('.app-nav-accordion .nav-gradient-owner')
      .forEach(el => el.classList.remove('nav-gradient-owner'));
  }

  getShortName(fullName) {
    return fullName.split(' ').map(n => n[0]).join('');
  }

  private buildBreadcrumbItems(url: string, extra: BreadcrumbItem[]): BreadcrumbItem[] {
    const extraItems = extra?.filter(item => !!item?.label) ?? [];
    const route = this.resolveBreadcrumbRoute(url);
    if (route.page === 'Dashboard' && extraItems.length === 0) {
      return [{ label: 'Dashboard' }];
    }

    const items: BreadcrumbItem[] = [];
    if (route.section) {
      items.push({
        label: route.section,
        link: MainComponent.SECTION_HOME_LINKS[route.section],
      });
    }
    const hasTrail = extraItems.length > 0 || !!route.tab;
    if (route.page) {
      items.push({
        label: route.page,
        link: hasTrail ? route.pageLink : undefined,
      });
    }
    if (route.tab) {
      items.push({ label: route.tab });
    }
    return [...items, ...extraItems];
  }

  private resolveBreadcrumbRoute(url: string): { section: string; page: string; pageLink?: string; tab?: string } {
    const segments = (url || '').replace(/^\/+/, '').split(/[/?#]/).filter(Boolean);
    const first = segments[0] || '';
    if (!first || first === 'dashboard') {
      return { section: '', page: 'Dashboard' };
    }
    if (first === 'demand') {
      const second = segments[1] || 'list';
      // New Connection is /demand/connections/create, not a Partners page.
      if (second === 'connections' && segments[2]) {
        return {
          section: 'Demand',
          page: 'Connections',
          pageLink: '/connections',
          tab: 'New Demand Connection',
        };
      }
      // Partner-scoped create/edit is /demand/details/:id/connections/create.
      if (second === 'details' && segments[3] === 'connections' && (segments[4] === 'create' || segments[4] === 'edit')) {
        return {
          section: 'Demand',
          page: 'Connections',
          pageLink: `/demand/details/${segments[2]}/connections`,
          tab: segments[4] === 'create' ? 'New Demand Connection' : 'Edit Demand Connection',
        };
      }
      const demandPage = MainComponent.DEMAND_PAGE_BY_SEGMENT[second] ?? MainComponent.DEMAND_PAGE_BY_SEGMENT.list;
      return {
        section: 'Demand',
        page: demandPage.page,
        pageLink: second === 'import-status' ? '/demand/list' : demandPage.pageLink,
        tab: MainComponent.demandTabLabel(second, segments[2]),
      };
    }
    if (first === 'campaigns') {
      const second = segments[1] || 'creative-library';
      const campaignsPage =
        MainComponent.CAMPAIGNS_PAGE_BY_SEGMENT[second] ??
        MainComponent.CAMPAIGNS_PAGE_BY_SEGMENT['creative-library'];
      return {
        section: 'Campaigns',
        page: campaignsPage.page,
        pageLink: campaignsPage.pageLink,
        tab: second === 'creative-library'
          ? MainComponent.CREATIVE_TAB_BY_SEGMENT[segments[2] || 'creatives-table']
          : undefined,
      };
    }
    if (first === 'ai-insights') {
      const tabBySegment: Record<string, string> = {
        recommendations: 'Recommendations',
        anomalies: 'Anomalies',
        yield: 'Yield',
        forecasts: 'Forecasts',
        settings: 'Settings',
      };
      return {
        section: '',
        page: 'AI Insights',
        pageLink: '/ai-insights/recommendations',
        tab: tabBySegment[segments[1] || 'recommendations'],
      };
    }
    if (first === 'taxonomy') {
      const mapped = MainComponent.PAGE_BY_SEGMENT.taxonomy;
      return {
        ...mapped,
        tab: segments[1] === 'genres'
          ? MainComponent.TAXONOMY_TAB_BY_SEGMENT[segments[2] || 'table']
          : undefined,
      };
    }
    const mapped = MainComponent.PAGE_BY_SEGMENT[first];
    if (mapped) {
      return { ...mapped, tab: MainComponent.inventoryTabLabel(first, segments[1]) };
    }
    return { section: '', page: first.charAt(0).toUpperCase() + first.slice(1) };
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}

@Directive({
  selector: 'mat-expansion-panel[ctvNavSectionGradient]',
  standalone: false
})
export class NavSectionGradientDirective implements OnDestroy {
  private readonly openedSub: Subscription;

  constructor(panel: MatExpansionPanel, private el: ElementRef<HTMLElement>) {
    this.openedSub = panel.opened.subscribe(() => {
      const accordion = this.el.nativeElement.closest('.app-nav-accordion');
      if (!accordion) {
        return;
      }
      accordion.querySelectorAll('.nav-gradient-owner').forEach(node => node.classList.remove('nav-gradient-owner'));
      this.el.nativeElement.classList.add('nav-gradient-owner');
    });
  }

  ngOnDestroy() {
    this.openedSub.unsubscribe();
  }
}

